import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { delayBadge } from "../utils/delayBadge";
import { routeScale } from "../utils/routeFit";

/**
 * 手で描いた路線図（2 次元）を、**節番号の線と同じ見た目**で描く。
 *
 * ## 座標は図のまま、見た目は既存のまま
 *
 * 図は Excel の均一なマス目で描いてある。(行, 列) をそのまま座標に写すので
 * 地形の判断は要らない。**描き方は `AnalysisLineMapPage` を踏襲する** —
 * 太い帯が線、白い札がバス停名、破線の札がランプ。
 *
 * ## 上下 2 本で引くのは幹と都市高速だけ
 *
 * 幹（3 号線・昭和通り。黒門〜特別支援学校前）と都市高速は**縦にまっすぐ**
 * なので、「左が上り（緑）・右が下り（青）」が最後まで成り立つ。**そのほかの
 * 線は 1 本**（車線 1 本ぶんの太さ）で、向きは三角とバスが持つ（`ROAD`）。
 *
 * 都市高速は**停留所が無い区間**なので白抜きにする。上り・下りはあるので
 * 帯 2 本の形は変えず、白抜きの枠として描く。
 *
 * ただし**クラスは流用しない。**あちらの `.lm-plate` や `.lm-bypass__ramp`
 * は `--lm-axis` を前提に位置を決めているので、ここで使うと効かない値が
 * 混ざる。見た目の値だけ写して `.ld-*` に持つ。
 *
 * ## 列の幅は名前で決める
 *
 * 行は均一（22px）。**列は名前が入るだけ広げる** — Excel は名前を右へ
 * はみ出させて描くので、同じことを幅で表す。名前の無い列は詰める。
 *
 * ## 帯は「車線 2 本」を別々に置く
 *
 * **マスごとに帯を置くのでは繋がらない。**列の幅が可変なので、横線をマスの
 * 幅（18px）で引くと広い列に 160px の隙間ができる。かといって中心どうしを
 * 太い帯で結ぶだけだと、**曲がり角で外側が 14px 角に欠ける**（どちらの帯も
 * 中心から先しか無いため）。
 *
 * だから**車線 1 本ずつを、中心線をずらした折れ線として置く。**北西へ 7.5px
 * ずらした線と、南東へ 7.5px ずらした線（どちらの向きが乗るかは `SIDE`。
 * **左側通行なので縦横で入れ替わる**）。直角の折れ線をこの向きにずらすと
 * **角の頂点もそのままずれる**ので、外側の車線は外回り・内側の車線は内回りに
 * なって欠けない（角・分岐・端には 13px 角の当てを 1 枚置いて継ぎ目を埋める）。
 *
 * 都市高速はこの形のまま、**同じ骨組みを 9px で白く塗り重ねる。**左右に
 * 2px だけ色が残って白抜きの枠になる。曲がっても枠が途切れない。
 *
 * ## 停留所のマスも線の一部
 *
 * Excel では**停留所のセルも塗ってある。**線と停留所を別に持っているので、
 * 線だけで隣を探すと**停留所のところで毎回切れる** — 線は「線・停留所・
 * 線・停留所」と交互に並ぶので、708 マスのうち 113 マスが孤立していた。
 * 繋がりを見るときは**線と停留所を合わせて**見る。
 */

/** 行の高さ（px）。停留所は 2 行おきなので、札 1 枚ぶんが 44px に収まる */
const ROW_H = 22;

/** 名前が無い列の幅（px）。**列の間隔ぶん掛ける**（Excel の空き列を詰める） */
const COL_MIN = 18;

/**
 * 札の 1 字ぶんと余白（px）。バス停は .84rem、ランプは .64rem。
 *
 * 余白には**両側の帯の半分（14px）と、帯の外への逃げ（18px）**を入れる。
 * 足りないと札の右端が**次の列の帯にかぶる**（48px で 35 枚・最大 7px）。
 */
const PLATE_CH = 13.4;
const PLATE_PAD = 56;
const RAMP_CH = 10.5;
const RAMP_PAD = 44;

/**
 * 縦書きの札（**横に走る帯のバス停**）。
 *
 * 横に走る帯のバス停名を右へ伸ばすと、その 1 枚のために列がまるごと広がる。
 * **縦書きにすれば列は札の幅ぶんで足りる**（実データで 5,684 → 4,667px）。
 *
 * `VERT_W` は札の幅（1 字＋左右の余白と枠）、`VERT_BOX` は字のほかに要る
 * 高さ（上下の余白と枠）、`VERT_NEED` はその列に取る幅 — **隣の列の札と
 * 当たらないぶん**（札は列の中心に立つので、左右に半分ずつ出る）。
 */
const VERT_W = 22;
const VERT_BOX = 14;
const VERT_NEED = VERT_W + 8;
/** 縦書きの 2 列目から 1 列ぶんの幅（px）。字の幅 × 行の高さ 1.25 */
const VERT_COL = Math.ceil(PLATE_CH * 1.25);

/** 札を帯の中心からどれだけ逃がすか（px）。帯の半分は 14px */
const LABEL_OFF = 16;

/**
 * 帯は車線 2 本（13px）＋隙間 2px = 28px。節番号の線と同じ太さ。
 *
 * **平行な線が隣の列・隣の行に来ることは無い**（図では必ず 2 マス以上
 * 離して描いてある。都市高速を含めて実データで 0 箇所と確かめた）ので、
 * 28px の帯でも隣の線とぶつからない。
 */
const BAR = 13;
const BAR_GAP = 2;
const BAND = BAR * 2 + BAR_GAP;

/** 車線の中心線をずらす量。帯の中心から ±7.5px（2 本で引く線） */
const LANE_OFF = (BAR + BAR_GAP) / 2;

/**
 * 1 本で引く線の、左右の寄せ（px）。
 *
 * 1 本の線は 13px しかないので、7.5px 寄せると**帯からはみ出す。**それでも
 * 左右には分ける — バスも三角も**進行方向の左側**に置くと決めてあり、
 * 真ん中に重ねると向きが読めなくなる。13px の中で左右に寄せる。
 */
const LANE_HALF = 3.5;

/** 都市高速の白抜きの内側（px）。左右に 2px ずつ色が残る */
const HOLE = BAR - 4;

/** 図の縁の余白（px）。**帯の半分が枠から出る**ので、その分だけ空ける */
const PAD = BAND / 2;

/**
 * 上下 2 本で引くのは**幹と都市高速だけ。**
 *
 * ## どの線も 2 本にすると、色が破綻する
 *
 * 節番号の線は 1 本の軸なので、どの点でも「上りは左・下りは右」と言えた。
 * **2 次元の図では言えない** — 道が曲がれば、同じ側の車線の上り下りが
 * 入れ替わる。全部の線を上下で塗り分けたら、**曲がり角ごとに帯の色が
 * 入れ替わり**、角の当てにどちらの色を置くかも決まらなかった。
 *
 * ## 幹（3 号線・昭和通り）と都市高速だけ 2 本にする
 *
 * この 2 本は**縦にまっすぐ**なので、「左が上り・右が下り」が最後まで
 * 成り立つ。黒門から特別支援学校前までの 1 本が幹（取り込みが `trunk` を
 * 付ける）。
 *
 * **そのほかは 1 本**（車線 1 本ぶんの太さ）。向きを塗り分けないので
 * 2 本に割る意味が無い。
 */
const ROAD = "#64748b";

/**
 * 上りと下り。**幹と都市高速の帯、三角、バスの色。**
 *
 * この図では 博多・天神 が左上なので、**北・西へ向かうのが上り**。
 * 幹は縦に走るので、左（西）が上り＝緑、右（東）が下り＝青。
 * 左側通行なので、**北行きは西側の車線＝左**で辻褄が合う。
 *
 * `ink` は**同じ色の濃いほう。**バスの位置の点と三角は帯の上に載るので、
 * 帯と同じ明るさだと溶けて見えない。色相は変えずに暗くする。
 */
const LANES = {
  backward: { key: "backward", color: "#0f9d58", ink: "#08492a" },
  forward: { key: "forward", color: "#1a73e8", ink: "#0b2f6b" },
};

/**
 * 向き → 上り下り（色）と、**帯のどちら側を走るか**（±1。北西が -1）。
 *
 * ## 日本は左側通行。**進行方向の左側を走る**
 *
 * 縦に走る道では、北行きは西側・南行きは東側。横に走る道では、**東行きが
 * 北側・西行きが南側。**
 *
 * 「上りはいつも北西側」にすると縦の道では合うが、**横の道で左右が
 * 入れ替わる** — 西行き（上り）を北側に置くことになり、照葉北小学校前から
 * 千早六丁目までのような東西の区間で、バスが道の反対側を走って見えた
 * （2026-09-18 に指摘を受けて直した）。
 */
const SIDE = {
  n: { lane: "backward", side: -1 },   // 北行き → 西側
  s: { lane: "forward", side: 1 },     // 南行き → 東側
  e: { lane: "forward", side: -1 },    // 東行き → 北側
  w: { lane: "backward", side: 1 },    // 西行き → 南側
};

/** 凡例で使う色。道・上り・下り */
export const RAIL_COLORS = [ROAD, LANES.backward.color, LANES.forward.color];

/** その向きの上り下り（色）。分からなければ下り */
function laneOfMove(move) {
  return LANES[SIDE[move]?.lane] ?? LANES.forward;
}

/** その向きが走る側（±1）。分からなければ南東 */
function sideOfMove(move) {
  return SIDE[move]?.side ?? 1;
}

/** 札に出す段。`label_lines`（2 段組みの名指し）が無ければ名前 1 段 */
function linesOf(stop) {
  return stop.label_lines?.length ? stop.label_lines : [stop.label];
}

/**
 * 列ごとの左端の x と、図の幅。
 *
 * **名前が入るだけ広げる。**名前が無ければ列の間隔ぶんだけ空ける（Excel の
 * 空き列はそのぶん詰まる）。
 *
 * ## 右へ出す札は、空いている列の上を渡ってよい
 *
 * 札はその行（と上下 1 行）に何も無い列の上なら重ならない。**広げるのは
 * 札が最初にぶつかる列までの間だけ**にする。香椎ランプの札（97px）は、
 * 右隣の列（御幸公園前の列）の 92 行あたりが空いているので、都市高速の
 * 列を 36px まで詰められる。
 *
 * ぶつかるのは、塗ったマス（線と停留所）・縦書きの札が下がっている
 * マス・左へ出す札が占めるマス。
 */
export function columnsOf(lines, stops, layout) {
  const plan = layout ?? layoutOf(lines, stops);
  const { kinds } = cellsOf(lines, stops);
  const need = new Map();
  const touch = (col) => {
    if (! need.has(col)) need.set(col, 0);

    return need.get(col);
  };
  // 左へ出す札。**右へ出す札と同じく、空いている列の上を渡ってよい**
  const leftward = [];
  // 右へ出す札。**どの列までに収まればよいか**はあとで決める
  const rightward = [];
  // 札がぶつかるマス（塗ったマスのほか）
  const taken = new Set(kinds.keys());
  for (const l of lines) touch(l.col);
  for (const s of stops) {
    // 2 段に組む札は**長いほうの段**の字数で取る
    const chars = Math.max(...linesOf(s).map((t) => [...t].length));
    const place = plan.get(`${s.row}/${s.col}`);
    touch(s.col);
    if (place?.vertical) {
      // **縦書きの札は列を広げない。**要るのは札の幅ぶんだけ。下がって
      // いる行は、ほかの札が渡れない
      // **2 列の縦書き**（`label_lines`）は 1 列ぶんずつ広い
      need.set(s.col, Math.max(
        need.get(s.col), VERT_NEED + (linesOf(s).length - 1) * VERT_COL
      ));
      const reach = LABEL_OFF + chars * PLATE_CH + VERT_BOX;
      const rows = Math.ceil((reach + ROW_H / 2) / ROW_H);
      for (let k = 1; k <= rows; k++) {
        taken.add(`${s.row + (place.down ? k : -k)}/${s.col}`);
      }

      continue;
    }
    const width = s.ramp
      ? chars * RAMP_CH + RAMP_PAD
      : chars * PLATE_CH + PLATE_PAD;
    if (place?.left) {
      leftward.push({ row: s.row, col: s.col, width });

      continue;
    }
    // ランプの札を支線のマスに置くときは、**そこから**右へ。下に敷くつなぎの
    // マスは渡ってよい（`layoutOf` の `anchor`）
    const from = place?.anchor ?? s;
    rightward.push({
      row: from.row, col: from.col, width, over: place?.over ?? null,
    });
  }

  const cols = [...need.keys()].sort((a, b) => a - b);
  const hits = (row, c) => [-1, 0, 1].some((dr) => taken.has(`${row + dr}/${c}`));
  // 左へ出す札が**最初にぶつかる列**（左へ向かって。無ければ null = 図の左端）。
  // 札はそこから自分の列までに収まればよい。渡る列は、右へ出す札が渡れない
  const lefts = leftward.map((l) => {
    const block = [...cols].reverse().find((c) => c < l.col && hits(l.row, c)) ?? null;

    return { ...l, block };
  });
  for (const l of lefts) {
    for (const c of cols) {
      if (c < l.col && (l.block === null || c > l.block)) {
        taken.add(`${l.row}/${c}`);
      }
    }
  }
  // 右へ出す札が**最初にぶつかる列**（無ければ null = 図の右端）。
  // 札はそこまでに収まればよい
  const ends = rightward.map((r) => ({
    ...r,
    to: cols.find((c) => c > r.col
      && [-1, 0, 1].some((dr) => taken.has(`${r.row + dr}/${c}`)
        && ! r.over?.has(`${r.row + dr}/${c}`))) ?? null,
  }));
  const x = new Map();
  let at = 0;
  cols.forEach((col, i) => {
    // 右へ出す札が**この列にぶつかるなら、その札が収まるところまで下げる**
    for (const e of ends) {
      if (e.to === col) {
        at = Math.max(at, x.get(e.col) + Math.ceil(e.width));
      }
    }
    // 左へ出す札も同じ。ぶつかる列（無ければ図の左端）から札のぶん離す
    for (const l of lefts) {
      if (l.col === col) {
        at = Math.max(at, l.block === null
          ? Math.ceil(l.width - COL_MIN / 2)
          : x.get(l.block) + Math.ceil(l.width));
      }
    }
    // **次に使う列までの間隔ぶんは最低限空ける。**Excel で 2 列離して
    // 描いてある線を、詰めて隣にしてしまわないため
    const gap = i + 1 < cols.length ? cols[i + 1] - col : 1;
    x.set(col, at);
    at += Math.max(COL_MIN * gap, Math.ceil(need.get(col)));
  });
  // 右端まで何にもぶつからない札は、図の幅で受ける
  for (const e of ends) {
    if (e.to === null) {
      at = Math.max(at, x.get(e.col) + Math.ceil(e.width));
    }
  }

  return { x, width: at, cols };
}

/**
 * 塗られたマスを集めて、種類を引ける表にする。
 *
 * **停留所のセルも塗ってある**ので合わせて見る。**同じマスは 1 つに畳む** —
 * 取り込みは線と停留所を分けて持つので普段は重ならないが、重なると同じ鍵の
 * 帯を 2 枚出すことになる。
 */
export function cellsOf(lines, stops) {
  const kinds = new Map();
  // **上下 2 本で引くマス。**幹（3 号線・昭和通り）と都市高速だけ
  const twins = new Set();
  for (const c of [...lines, ...(stops || []).filter((s) => s.kind)]) {
    const at = `${c.row}/${c.col}`;
    kinds.set(at, c.kind);
    if (c.trunk || c.kind === "express") {
      twins.add(at);
    }
  }
  // **ランプから支線までのつなぎは都市高速として描く**（見た目だけ）
  const stubs = rampApproaches(kinds, stops || []);
  for (const s of stubs) {
    for (const at of s.path) {
      kinds.set(at, "express");
      twins.add(at);
    }
  }
  const cells = [...kinds.entries()].map(([at, kind]) => {
    const [row, col] = at.split("/").map(Number);

    return { row, col, kind, twin: twins.has(at) };
  });
  const stubCells = new Set(stubs.flatMap((s) => s.path));
  // **下りに付いた出入口のランプ**（API の `ramp_outbound`）。┐ の角と同じ組
  const crossed = new Set((stops || [])
    .filter((s) => s.ramp && s.ramp_outbound)
    .map((s) => `${s.row}/${s.col}`));

  const ups = expressUps(kinds, stubCells);
  stubUps(ups, stubs, kinds, crossed);

  return { kinds, twins, cells, stubs, ups, crossed };
}

/**
 * ランプのつなぎの上り下り。**合流するランプの車線と同じ色にする。**
 *
 * 帯は同じ側どうしで繋がる（北側の車線は、縦の帯の西側の車線へ入る）。
 * 東西南北の決まりのまま塗ると、呉服町ランプでは**つなぎの緑が都市高速の
 * 青へ入っていた**（2026-09-23 に指摘を受けて直した）。
 *
 * 縦の帯で西側（-1）が緑なのは上りが北のとき。横のつなぎで北側（-1）が
 * 緑なのは上りが東のとき。だから 北 → 東、南 → 西（横から縦は その逆）。
 *
 * **ランプが ┐ か └ の角なら組が逆**（`flippedCorner`）。天神北ランプは
 * 都市高速の端で、つなぎへ左に曲がる。北行き（西側・緑）はそのまま
 * 西行き（南側）になるので、北 → 西。
 *
 * **丁字のランプは形では決まらない。**既定（北 → 東）は天神方面の出入口
 * （上りへ左折で入る。名島・香椎浜）。呉服町は香椎方面の出入口で、上りから
 * 左折で出るので天神北と同じ北 → 西（`ramp_outbound`。2026-09-24 に指摘を
 * 受けて直した）。
 */
function stubUps(ups, stubs, kinds, crossed) {
  const turn = { n: "e", s: "w", e: "n", w: "s" };
  const flip = { n: "w", s: "e", e: "s", w: "n" };
  for (const stub of stubs) {
    const at = ups.get(stub.ramp);
    const [row, col] = stub.ramp.split("/").map(Number);
    const map = flippedCorner(kinds, { row, col, kind: "express" }, crossed)
      ? flip : turn;
    const axis = stub.dc !== 0 ? "h" : "v";
    const cross = axis === "h" ? "v" : "h";
    // 同じ向きに続くなら**そのまま**、直角に出るなら入る車線の色に合わせる
    const up = at?.[axis] ?? (at?.[cross] ? map[at[cross]] : null);
    if (! up) {
      continue;
    }
    at[axis] ??= up;
    for (const cell of stub.path) {
      ups.set(cell, { [axis]: up });
    }
  }
}

const BACK = { n: "s", s: "n", e: "w", w: "e" };
const STEPS = { n: [-1, 0], s: [1, 0], w: [0, -1], e: [0, 1] };

/**
 * 都市高速の各マスで、**上り（天神・博多へ）はどちら向きか。**縦と横で別に持つ。
 *
 * ## 東西南北では決められない
 *
 * 幹は縦にまっすぐなので「北行き＝上り」で足りたが、**都市高速は曲がる。**
 * 香椎東から天神へ向かう便は 96 行・90 行を**東へ**走り、アイランドシティ
 * から来る便は 34 列を**南へ**走る。「北・西が上り」で塗ると、そこだけ
 * 緑と青が入れ替わっていた（2026-09-23 に指摘を受けて直した）。
 *
 * ## 天神の端からたどる
 *
 * 都市高速のいちばん上（天神北ランプ）から線をたどり、**たどってきた向きの
 * 逆が上り。**分かれ目（香椎浜の東への枝）でもそのまま正しい。
 *
 * 曲がり角のマスは縦と横の両方に帯を出すので、両方の軸の向きを持たせる。
 * ランプのつなぎ（見た目だけ都市高速）はここでは たどらない。たどると
 * 天神北ランプのつなぎが「いちばん上の端」になってしまう。つなぎの色は
 * あとで、合流するランプの車線に合わせる（`stubUps`）。
 *
 * @return Map<string, {v?: string, h?: string}>
 */
function expressUps(kinds, skip) {
  const ups = new Map();
  const express = [...kinds.entries()]
    .filter(([at, kind]) => kind === "express" && ! skip.has(at))
    .map(([at]) => at.split("/").map(Number))
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (express.length === 0) {
    return ups;
  }
  const axisOfMove = (m) => (m === "n" || m === "s" ? "v" : "h");
  const set = (at, move) => {
    const u = ups.get(at) ?? {};
    u[axisOfMove(move)] ??= move;
    ups.set(at, u);
  };
  const [r0, c0] = express[0];
  const seen = new Set([`${r0}/${c0}`]);
  const queue = [[r0, c0]];
  while (queue.length) {
    const [row, col] = queue.shift();
    const cell = { row, col, kind: "express" };
    for (const [move, [dr, dc]] of Object.entries(STEPS)) {
      // 交差で切ってあるところは 1 マス飛ぶ（`reach`）
      const n = reach(kinds, cell, dr, dc);
      const at = `${row + dr * n}/${col + dc * n}`;
      if (! n || seen.has(at) || skip.has(at)) {
        continue;
      }
      seen.add(at);
      // 上りは**たどってきた向きの逆**。どちらのマスでもこの軸の上り
      set(at, BACK[move]);
      set(`${row}/${col}`, BACK[move]);
      queue.push([row + dr * n, col + dc * n]);
    }
  }

  return ups;
}

/**
 * ランプから支線へのつなぎ。
 *
 * 図ではランプの隣から**支線の色（黒）で**塗ってあり、そのまま描くと支線が
 * 高速まで伸びてきたように見える。つなぎは高速の出入口なので、**都市高速の
 * ほうを伸ばして描く。**
 *
 * ランプから、まっすぐ続く支線のマスをたどる。**バス停・分かれ目・曲がり角**に
 * 着いたらそこまで（そのマスは支線のまま。`to`）。
 *
 * **塗りの種類は描くときだけ変える。**API の道探し（`LineDiagram::routeSteps`）は
 * 種類が変わる step を通さないので、データを塗り替えるとランプを通る便の道が
 * 切れる。
 *
 * @return Array<{path: string[], from: string, to: string|null, dr: number, dc: number}>
 */
function rampApproaches(kinds, stops) {
  const busStops = new Set(
    stops.filter((s) => ! s.ramp).map((s) => `${s.row}/${s.col}`)
  );
  const key = (r, c) => `${r}/${c}`;
  const degree = (r, c) => [[-1, 0], [1, 0], [0, -1], [0, 1]]
    .filter(([dr, dc]) => kinds.has(key(r + dr, c + dc))).length;
  const out = [];
  for (const ramp of stops.filter((s) => s.ramp && s.kind === "express")) {
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      let r = ramp.row + dr;
      let c = ramp.col + dc;
      if (kinds.get(key(r, c)) !== "route") {
        continue;
      }
      const path = [];
      // まっすぐ続く支線のマス（前と後ろにだけ隣がある）
      while (kinds.get(key(r, c)) === "route"
        && ! busStops.has(key(r, c))
        && degree(r, c) === 2
        && kinds.has(key(r + dr, c + dc))) {
        path.push(key(r, c));
        r += dr;
        c += dc;
      }
      out.push({
        path,
        ramp: key(ramp.row, ramp.col),
        from: path.length ? path[path.length - 1] : key(ramp.row, ramp.col),
        to: kinds.has(key(r, c)) ? key(r, c) : null,
        dr, dc,
      });
    }
  }

  return out;
}

/**
 * 札の置き方を停留所ごとに決める。**画素を使わずに決める。**
 *
 * ## 横に走る帯のバス停は縦書き
 *
 * 名前を右へ伸ばすと、その 1 枚のために列がまるごと広がる（`アイランド
 * シティ中央公園前` は 13 字で 230px）。横に走る帯のバス停は**帯の上か下へ
 * 縦書きで下げる**ことにした。実データでは 45 枚のうち 42 枚が縦書きになり、
 * 図の幅が 5,684 → 4,667px（-18%）になる。
 *
 * ## 入るかどうかは行と列で見る
 *
 * 縦書きの札は下（または上）へ伸びるので、**そこに別の線があると重なる。**
 * 札が届く行を数えて、その範囲に塗ったマスが無いことを確かめる。
 *
 * **画素で測らない。**列の幅は「縦書きにするか」で変わり、縦書きにするかは
 * 位置で決まるので、画素で測ると鶏と卵になる。隣の列（±1）まで見ておけば、
 * 2 列離れたマスとは必ず 36px 以上あるので当たらない。
 *
 * 入らなければ横のまま（実データで 3 枚。香椎浜中央のように、4 行しか
 * 空いていない平行な線のあいだに立っているもの）。
 *
 * ## 左へ出す札
 *
 * 横書きの札は右へ出すが、API が `label_left` を付けたバス停は左へ出す
 * （`config/linemap.php`。右に別の線が迫っているところ）。縦書きには効かない。
 *
 * `label_vertical`（'up' / 'down'）が付いたバス停は、帯の向きにかかわらず
 * 縦書きにする。
 *
 * @return Map<string, {axis: string, vertical: boolean, down: boolean, left: boolean}>
 */
export function layoutOf(lines, stops) {
  const { kinds, stubs } = cellsOf(lines, stops);
  // ランプ → 西へ出るつなぎ（`rampApproaches`）
  const westward = new Map(stubs
    .filter((s) => s.dc === -1 && s.to !== null)
    .map((s) => [s.ramp, s]));
  const out = new Map();
  for (const stop of stops) {
    const axis = axisOf(kinds, stop);
    /* **ランプの札は、つなぎが支線に着くところに置く。**
       札は右へ出すので、つなぎが東へ出るランプ（アイランドシティ）は
       そのままでつなぎの上に載る。**西へ出るランプ**（天神北・呉服町・
       名島・香椎浜・香椎）は、右へ出すと都市高速の向こう側になり、どの
       支線へ降りるのか読めない。支線のマスから右へ、つなぎの上に出す */
    const stub = stop.ramp && westward.get(`${stop.row}/${stop.col}`);
    if (stub) {
      const [row, col] = stub.to.split("/").map(Number);
      out.set(`${stop.row}/${stop.col}`, {
        axis, vertical: false, down: true, left: false,
        anchor: { row, col }, over: new Set(stub.path),
      });

      continue;
    }
    const left = !! stop.label_left && ! stop.ramp;
    // **名指しの縦書き**（丁字の角など、縦にも帯が出ているところ）。
    // 入るかどうかは確かめない — 名指しした人が図を見て決めている
    if (stop.label_vertical && ! stop.ramp) {
      out.set(`${stop.row}/${stop.col}`, {
        axis, vertical: true, down: stop.label_vertical === "down", left: false,
      });

      continue;
    }
    // 縦に走る帯のバス停と、ランプの札は右へ出す（節番号の線と同じ）
    if (axis !== "h" || stop.ramp || ! stop.kind) {
      out.set(`${stop.row}/${stop.col}`, {
        axis, vertical: false, down: true, left,
      });

      continue;
    }
    const need = [...stop.label].length * PLATE_CH + VERT_BOX;
    const down = roomOf(kinds, stop, 1) >= need;
    const up = ! down && roomOf(kinds, stop, -1) >= need;
    out.set(`${stop.row}/${stop.col}`, {
      axis, vertical: down || up, down: down || ! up,
      left: left && ! (down || up),
    });
  }

  return out;
}

/**
 * その向きに線が続いているか。**続いていれば何マス先か**（0 / 1 / 2）。
 *
 * ## 交差はまたぐ
 *
 * 図では**交差するところで片方の線を 1 マス切ってある。**都市高速は
 * 26 列を南北に走り、東西の路線と交わる 7 箇所で切れている（29 行と
 * 31 行が都市高速で、30 行は路線）。逆に 96 行では都市高速が横切って
 * 3 号線（16 列）が切れている。
 *
 * そのまま描くと**線が交差ごとに途切れる。**別の線が 1 マスだけ挟まって
 * いるときは、その先へ繋ぐ（実データで 8 箇所。都市高速が 7・3 号線が 1）。
 *
 * **空いている 1 マスはまたがない。**平行な線が 2 行離れて並んでいる
 * ところ（60 箇所）を繋いでしまう。
 */
function reach(kinds, cell, dr, dc) {
  const next = kinds.get(`${cell.row + dr}/${cell.col + dc}`);
  if (next === cell.kind) {
    return 1;
  }
  if (next !== undefined
    && kinds.get(`${cell.row + dr * 2}/${cell.col + dc * 2}`) === cell.kind) {
    return 2;
  }

  return 0;
}

/** その停留所の帯が縦に走るか横に走るか */
function axisOf(kinds, cell) {
  const hasV = reach(kinds, cell, -1, 0) || reach(kinds, cell, 1, 0)
    || (! reach(kinds, cell, 0, -1) && ! reach(kinds, cell, 0, 1));

  return hasV ? "v" : "h";
}

/**
 * 縦書きの札が伸ばせる高さ（px）。**隣のマスの帯の縁まで。**
 *
 * `k` 行先に塗ったマスがあれば、その帯の上端は `k * 22 - 14`。札は帯の
 * 中心から 16px 逃がして始めるので、入るのは `k * 22 - 14 - 16` まで。
 */
function roomOf(kinds, cell, dir) {
  for (let k = 1; k <= 30; k++) {
    for (const dc of [-1, 0, 1]) {
      if (kinds.has(`${cell.row + dir * k}/${cell.col + dc}`)) {
        return k * ROW_H - BAND / 2 - LABEL_OFF;
      }
    }
  }

  return 30 * ROW_H;
}

/**
 * そのマスの、その車線の位置（縦に走るなら横へ、横に走るなら縦へずらす）。
 *
 * 隣の様子も返す — 帯を伸ばす先と、継ぎ目の当てが要るかの判断に使う。
 */
/**
 * ┐ か └ の角か。
 *
 * 車線は「縦の西側と横の北側」「縦の東側と横の南側」を組にして、角の頂点を
 * 斜めにずらしている。┘ と ┌ ではこれで**左側通行のまま曲がれる**
 * （┘ なら、南行きの東側 → 西行きの南側）。
 *
 * **┐ と └ では組が逆になる。**┐ で北行きの西側の車線が左へ曲がると、
 * 西行きの南側に出なければならない。組のまま描くと北側へ出て**逆走**に
 * 見えた（天神北ランプ。2026-09-23 に指摘を受けて直した）。
 *
 * 都市高速の角はすべて ┘ か ┌ で、┐ はランプのつなぎが付いた天神北ランプ
 * だけ。1 本で引く線は車線を寄せないので関わらない。
 *
 * **下りに付いた出入口のランプ**（`crossed`。呉服町）も同じ組にする。
 * 丁字で西へつなぎが出るとき、上りの西側の車線が左折して**西行きの南側**へ
 * 出る（下りへ入る北側の車線は、上りの車線を渡って東側へ入る）。
 */
function flippedCorner(kinds, cell, crossed) {
  if (crossed?.has(`${cell.row}/${cell.col}`)) {
    return true;
  }
  const up = reach(kinds, cell, -1, 0);
  const down = reach(kinds, cell, 1, 0);
  const left = reach(kinds, cell, 0, -1);
  const right = reach(kinds, cell, 0, 1);

  return (!! left && !! down && ! up && ! right)
    || (!! up && !! right && ! left && ! down);
}

function laneOf(kinds, cx, cy, cell, sign, crossed) {
  // **何マス先で続くか。**交差では 1 マス飛ぶ（2 になる）
  const up = reach(kinds, cell, -1, 0);
  const down = reach(kinds, cell, 1, 0);
  const left = reach(kinds, cell, 0, -1);
  const right = reach(kinds, cell, 0, 1);
  // **1 マスだけの線は縦向きに置く**（線が消えるより、そこに何かあると
  // 分かるほうがよい）
  const hasV = up || down || (! left && ! right);
  const hasH = left || right;

  return {
    up,
    down,
    left,
    right,
    // 縦に走るなら上り・下りは左右に、横に走るなら上下に分かれる
    // （**判断は `axisOf` に 1 つだけ置く**）
    axis: axisOf(kinds, cell),
    x: cx(cell.col) + (hasV ? sign * LANE_OFF : 0),
    // ┐ と └ の角では、縦の車線と横の車線が**斜め逆**で繋がる（`flippedCorner`）
    y: cy(cell.row) + (hasH ? sign * LANE_OFF : 0)
      * (flippedCorner(kinds, cell, crossed) ? -1 : 1),
    // まっすぐ通り抜けるだけの節（当てが要らない）
    pass: (up && down && ! hasH) || (left && right && ! up && ! down),
  };
}

/**
 * 停まる向きの三角。**帯の上に置く。**
 *
 * どちらの向きが停まるかは時刻表から出してある（`stops[].moves` = その停留所
 * を出ていく向き）。**帯のどちら側に置くかはここ** — 進行方向の左側
 * （`SIDE`）に置くので、バスが載る車線と必ず揃う。片方しか無ければ三角も
 * 1 つで、「帯は通っているのに停まらない」が読める。
 *
 * 時刻表に無い停留所と、その日に便が無い停留所は `moves` が空なので何も
 * 描かない（**無いものを描かない。**節番号の線の `only` と違って、手で
 * 書いた断りではないため）。
 */
/**
 * その停留所に三角を立てる向き。**帯の片側につき 1 つ。**
 *
 * 道には側が 2 つしかないので、三角も 2 つまで。**同じ側になる向きが
 * 2 つある**ことがあり（北行きと東行きはどちらも左＝北西側、南行きと西行きは
 * 南東側）、そのまま描くと**同じ点に 2 枚重なる。**
 *
 * どちらを残すかは**帯の走る向きに合うほう** — 縦に走る帯なら n/s、横に
 * 走る帯なら e/w。曲がり角の停留所では両方が帯に沿うので、先に来たほうを
 * 採る（どちらでも位置は同じ）。
 */
function sidesOf(kinds, stop) {
  const axis = axisOf(kinds, stop);
  const pick = new Map();
  for (const move of stop.moves || []) {
    if (! SIDE[move]) {
      continue;
    }
    const side = sideOfMove(move);
    const fits = (move === "n" || move === "s") === (axis === "v");
    if (! pick.has(side) || (fits && ! pick.get(side).fits)) {
      pick.set(side, { move, fits });
    }
  }

  return [...pick.values()].map((x) => x.move);
}

export function marksOf(lines, stops, cx, cy) {
  const { kinds, twins } = cellsOf(lines, stops);
  const out = [];
  for (const stop of stops) {
    if (! stop.kind) {
      continue;
    }
    for (const move of sidesOf(kinds, stop)) {
      // **バスと同じ寄せ方**（1 本の線は帯の中で左右へ）。走る向きの軸に
      // 直交する側へ寄せるだけで、角でも斜めにはならない
      const vertical = move === "n" || move === "s";
      const off = (twins.has(`${stop.row}/${stop.col}`) ? LANE_OFF : LANE_HALF)
        * sideOfMove(move);
      out.push({
        key: `${stop.row}/${stop.col}/${move}`,
        dir: laneOfMove(move).key,
        // **1 本で引く線では、向きは三角の色で持つ。**濃いほうで塗る
        // （帯の灰の上では、明るい青緑は沈む）
        ink: laneOfMove(move).ink,
        // **三角の形はその向きで決める**（縦に走る向きなら上下、横なら左右）
        axis: vertical ? "v" : "h",
        left: cx(stop.col) + (vertical ? off : 0),
        top: cy(stop.row) + (vertical ? 0 : off),
      });
    }
  }

  return out;
}

/**
 * バスの枠の大きさ（px）。**節番号の線と同じだけの中身を載せる** —
 * 営業所の頭 1 文字・車号・行先番号・遅れ。
 *
 * 帯の上に置くと 13px の車線に収まらないので、**枠は帯の脇へ出して、
 * 正確な位置には点を残す。**出す側は**そのバスが走っている車線と同じ**
 * （左側通行）なので、すれ違う 2 台の枠はぶつからない。
 *
 * 値は CSS の `.ld-bus` と揃えること — **積み分けがこの値で重なりを見る。**
 */
const CARD_W = 118;
const CARD_H = 18;

/** 積むときのあいだ（px）。**触れていないと分かるだけ空ける** */
const BUS_GAP = 3;

/** 点から枠までの距離（px）。CSS の `.ld-mover.is-* .ld-bus` の 8px と揃える */
const CARD_OFFSET = 8;

/** 進む向きと、その逆（積むときに後ろへ下げる向き） */
const MOVE_AT = {
  n: [0, -1], s: [0, 1], e: [1, 0], w: [-1, 0],
};

/**
 * その区間の道（マスの並び）。**バスの進む向きに揃えて返す。**
 *
 * 道は API が区間ごとに 1 本だけ返す（上りと下りで同じ道なので畳んである）。
 * 鍵は「小さいほうのマス|大きいほう」なので、**逆向きの便では並べ直す。**
 *
 * 道が引けていない区間（図の上で繋がっていない）は 2 点を直に結ぶ。
 * **黙って動かさないより、直線でも動かしたほうが「そこにいる」と読める。**
 */
/**
 * 都市高速の上にいるバスの色（上りなら緑）。都市高速でなければ null で、
 * 東西南北の決まり（`laneOfMove`）のまま。
 */
function paintOf(grid, cells, spot, cx, cy) {
  if (! grid.ups || ! spot.move) {
    return null;
  }
  // いまいるマス = 道のうち、中心がいちばん近いマス
  const near = cells.reduce((best, c) => {
    const d = Math.abs(cy(c[0]) - spot.top) + Math.abs(cx(c[1]) - spot.left);

    return best === null || d < best.d ? { c, d } : best;
  }, null);
  if (! near) {
    return null;
  }
  const at = `${near.c[0]}/${near.c[1]}`;
  const axis = spot.move === "n" || spot.move === "s" ? "v" : "h";
  const up = grid.kinds.get(at) === "express" ? grid.ups.get(at)?.[axis] : null;
  if (! up) {
    return null;
  }

  return spot.move === up ? LANES.backward : LANES.forward;
}

function legCells(corridors, step, next) {
  if (! next) {
    return [[step.row, step.col]];
  }
  const from = `${step.row}/${step.col}`;
  const to = `${next.row}/${next.col}`;
  const cells = (corridors || {})[from <= to ? `${from}|${to}` : `${to}|${from}`];
  if (! cells || cells.length === 0) {
    return [[step.row, step.col], [next.row, next.col]];
  }

  return cells[0][0] === step.row && cells[0][1] === step.col
    ? cells
    : [...cells].reverse();
}

/** 隣り合う 2 マスのあいだの向き。**長いほうを採る**（1 歩なら片方だけ動く） */
function dirOf(from, to) {
  const dr = to[0] - from[0];
  const dc = to[1] - from[1];

  return Math.abs(dr) >= Math.abs(dc)
    ? (dr < 0 ? 'n' : 's')
    : (dc < 0 ? 'w' : 'e');
}

/**
 * 道を、車線の位置に直した折れ線にする。**車線は 1 歩ごとに決める。**
 *
 * 帯の左右は、そのままその道の上り・下りなので、**バスはいま走っている
 * 向きの側に載っていなければならない**（左側通行。`SIDE`）。区間ぜんたいで
 * 1 つに決めると、曲がる道で反対側に載る — 照葉小中学校前から照葉北小学校前へは
 * **東へ走ってから北へ上がる**ので、東のあいだ逆の車線になっていた。
 *
 * ## 角はスムーズに曲がる
 *
 * 曲がるところでは、**縦の車線と横の車線が交わる点**を通す。
 *
 *     和白（108,16）→ 令和健康大学前（110,18）は南へ 2 マス・東へ 2 マス。
 *     南行きは東側（x = 中心 + 7.5）、東行きは北側（y = 中心 - 7.5）で、
 *     2 本は (中心 + 7.5, 中心 - 7.5) で交わる。そこを通れば直角に曲がる。
 *
 * **マスの隅で取ってはいけない。**前は角のマスの位置を「北西の隅」「南東の
 * 隅」で取っていたので、南行き（南東の隅）から東行き（北西の隅）へ移るときに
 * **帯を斜めに横切ってから曲がっていた**（2026-09-19 に指摘を受けて直した）。
 *
 * だから点の座標は**軸ごとに別々に決める** — 縦に走る区間が x を、横に走る
 * 区間が y を決める。折り返し（同じ軸で側が入れ替わる）だけは本当に道を
 * 渡るので、2 点を置いて渡らせる。
 *
 * 点には**そこから先へ走る向き**を持たせる。
 */
function lanePoints(kinds, twins, cx, cy, cells, fallback) {
  // 車線の中心。**2 本で引く線は帯の半分ずつ、1 本の線はその中の左右**
  const offOf = (cell) => (twins.has(`${cell[0]}/${cell[1]}`) ? LANE_OFF : LANE_HALF);
  const isV = (move) => move === "n" || move === "s";

  if (cells.length < 2) {
    const off = offOf(cells[0]) * sideOfMove(fallback);

    return [{
      x: cx(cells[0][1]) + (isV(fallback) ? off : 0),
      y: cy(cells[0][0]) + (isV(fallback) ? 0 : off),
      move: fallback,
    }];
  }

  // 区間ごとの向きと、走る側
  const segs = [];
  for (let i = 1; i < cells.length; i++) {
    const move = dirOf(cells[i - 1], cells[i]);
    segs.push({ move, side: sideOfMove(move), vertical: isV(move) });
  }

  const out = [];
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const off = offOf(cell);
    const into = segs[i - 1] || null;    // 入ってくる区間
    const from = segs[i] || null;        // 出ていく区間
    const move = (from || into).move;

    // **軸ごとに、その軸を走る区間が決める。**角では縦が x を・横が y を
    const legs = [into, from].filter(Boolean);
    const vert = legs.find((s) => s.vertical);
    const horz = legs.find((s) => ! s.vertical);
    const x = cx(cell[1]) + (vert ? vert.side * off : 0);
    const y = cy(cell[0]) + (horz ? horz.side * off : 0);

    // **折り返しは本当に道を渡る。**同じ軸で側が入れ替わるところ（終点で
    // 折り返す便）だけ、渡る 1 本を置く
    if (into && from && into.vertical === from.vertical && into.side !== from.side) {
      out.push(into.vertical
        ? { x: cx(cell[1]) + into.side * off, y, move }
        : { x, y: cy(cell[0]) + into.side * off, move });
    }
    out.push({ x, y, move });
  }

  return out;
}

/**
 * 折れ線の、道のりで `ratio` のところ。**マスの数ではなく長さで測る。**
 *
 * マスの数で割ると、列の幅が可変なので**広い列を一瞬で飛び、狭い列で
 * のろのろ進む。**長さで割れば図の上の速さが揃う。
 *
 * 向き（n/s/e/w）も返す — **その 1 本を走っている向き。**車線も矢印も
 * これで決まる。
 */
function pointAt(points, ratio) {
  const spans = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const len = Math.hypot(
      points[i].x - points[i - 1].x, points[i].y - points[i - 1].y
    );
    spans.push(len);
    total += len;
  }
  if (points.length < 2 || total === 0) {
    return { left: points[0].x, top: points[0].y, move: points[0].move };
  }

  let want = Math.max(0, Math.min(1, ratio)) * total;
  for (let i = 0; i < spans.length; i++) {
    if (want > spans[i] && i < spans.length - 1) {
      want -= spans[i];

      continue;
    }
    const a = points[i];
    const b = points[i + 1];
    const t = spans[i] === 0 ? 0 : Math.min(1, want / spans[i]);

    return {
      left: a.x + (b.x - a.x) * t,
      top: a.y + (b.y - a.y) * t,
      move: a.move,
    };
  }

  return { left: points[0].x, top: points[0].y, move: points[0].move };
}

/**
 * バスを図の上に置く。
 *
 * 区間のどこまで来たか（`ratio`）は時刻表の所要から出ていて、**そこから先の
 * 見え方だけ**がここの仕事 — その区間の道をたどり、車線の位置へ寄せる。
 *
 * **停まっているバスは道の起点**（ratio を 0 にする）。停留所のマスの、その
 * 車線の位置なので、三角の隣に載る。
 *
 * @param {Array} movers `advance` を通したバス（index / ratio / moving）
 * @param {Object} corridors 区間ごとの道
 * @param {{kinds: Map, twins: Set}} grid 塗ったマスと、2 本で引くマス
 */
export function busesOf(movers, corridors, grid, cx, cy) {
  const { kinds, twins } = grid;
  const out = [];
  for (const mover of movers) {
    const path = mover.bus.path || [];
    const step = path[mover.index];
    if (! step) {
      continue;
    }
    const next = path[mover.index + 1] || null;
    // **車線はいま走っている 1 歩で決める。**区間ぜんたいで 1 つに決めると、
    // 曲がる道で反対側の車線に載る（`lanePoints`）
    const points = lanePoints(
      kinds, twins, cx, cy, legCells(corridors, step, next), step.move
    );
    const spot = pointAt(points, mover.moving ? mover.ratio : 0);
    const lane = laneOfMove(spot.move);
    // **色は帯に合わせる。**都市高速の上では、たどった上りで決める
    // （`expressUps`）。`lane.key` は変えない — 詳細を開くときの向きに使う
    const paint = paintOf(grid, legCells(corridors, step, next), spot, cx, cy) ?? lane;

    out.push({
      ...mover,
      lane: lane.key,
      color: paint.color,
      ink: paint.ink,
      express: !! step.express,
      ...spot,
    });
  }

  return stackBuses(out);
}

/**
 * 重なったバスを、**進む向きの後ろへ**ずらす。
 *
 * 同じ停留所に 2 台並んでいると、重ねたままでは台数が読めない。前へ
 * ずらすと、まだ着いていない停留所にいるように見えるので**後ろへ**。
 *
 * **先頭は本当の位置に置く。**進んでいるほうから順に置いて、後ろのものを
 * 下げる。ずらした 2 台目以降は本当の位置を指さない — 台数が読めないほうが
 * 困るのでそこは譲る（節番号の線の `stackOffsets` と同じ割り切り）。
 *
 * ## 横に走るバスは、枠を外側の段へ出す
 *
 * **横の枠は 118px あり、停留所 1 つぶんより長い。**後ろへ枠の幅ぶん下げると、
 * 隣の停留所にいるだけの 2 台目が 121px も後ろへ飛び、本当の位置から離れた
 * （2026-09-24 に指摘）。横に走るバスは**点を動かさず、枠だけを帯から外側へ
 * 1 段ずつ**出す（`row`）。
 *
 *     ー●  ●ーー
 *      ◀ア8072 22N +2
 *            ◀ア1132 1 +9
 *
 * 段が埋まったら（`ROWS` 段）、これまでどおり後ろへ下げる。縦に走るバスは
 * 枠の高さ（18px）ぶん下げれば済むので、段は使わない。
 *
 * **点は重ねない。**同じ停留所に 2 台いると点がぴったり重なり、台数が
 * 読めない（2026-09-24 に指摘）。後ろの点を**点が触れないだけ**（`DOT_STEP`）
 * 後ろへずらし、枠は段で分ける。
 */
function stackBuses(buses) {
  const ahead = (b) => {
    const [dx, dy] = MOVE_AT[b.move] || [0, 1];

    return b.left * dx + b.top * dy;
  };
  const sorted = [...buses].sort((a, b) => ahead(b) - ahead(a));

  // **枠どうしで見る。**段を変えると、点は同じでも枠は離れる
  const hits = (done, bus) => {
    const a = cardOf(bus);

    return done.some((d) => {
      if (d.lane !== bus.lane) return false;
      const b = cardOf(d);

      return Math.abs(a.x - b.x) < (a.w + b.w) / 2 + BUS_GAP
        && Math.abs(a.y - b.y) < (a.h + b.h) / 2 + BUS_GAP;
    });
  };

  // 点どうし。**同じ車線だけ**（反対の車線は帯の反対側に載る）
  const dotHits = (done, bus) => done.some((d) => d.lane === bus.lane
    && Math.abs(d.left - bus.left) < DOT_STEP
    && Math.abs(d.top - bus.top) < DOT_STEP);

  const done = [];
  for (const bus of sorted) {
    let placed = { ...bus, row: 0 };
    if (bus.move === 'e' || bus.move === 'w') {
      const [dx] = MOVE_AT[bus.move];
      // **点が触れないだけ後ろへ。**枠は次で段に分けるので、ここは点の分だけ
      for (let guard = 0; guard < 8 && dotHits(done, placed); guard++) {
        placed = { ...placed, left: placed.left - dx * DOT_STEP };
      }
      while (placed.row < ROWS - 1 && hits(done, placed)) {
        placed = { ...placed, row: placed.row + 1 };
      }
      if (hits(done, placed)) {
        placed = { ...placed, row: 0 };
      }
    }
    const [dx, dy] = MOVE_AT[bus.move] || [0, 1];
    // 下げる量は**進む向きの枠の長さ**。縦に走る帯では枠の高さで足りる
    const step = alongOf(bus) + BUS_GAP;
    for (let guard = 0; guard < 8 && hits(done, placed); guard++) {
      placed = { ...placed, left: placed.left - dx * step, top: placed.top - dy * step };
    }
    done.push(placed);
  }

  return done;
}

/** 横に走るバスの枠を、帯から外側へ何段まで出すか */
const ROWS = 3;

/**
 * 並んだ点のあいだ（px）。点の径 11px（CSS の `.ld-mover__dot`）＋あいだ 3px。
 * **触れていないと分かるだけ**で、それ以上は離さない（本当の位置から遠ざかる）
 */
const DOT_STEP = 11 + BUS_GAP;

/** 進む向きの、枠の長さ（px）。縦に走るなら高さ、横に走るなら幅 */
function alongOf(bus) {
  return bus.move === 'n' || bus.move === 's' ? CARD_H : CARD_W;
}

/**
 * 枠の中心と大きさ（px）。**CSS の `.ld-mover.is-* .ld-bus` と揃える。**
 *
 * 枠は帯の脇（点から `CARD_OFFSET`）へ、そのバスの車線の側に出る。
 * 横に走るバスは中心を点に揃え、`row` 段ぶん外へ出る。
 */
function cardOf(bus) {
  const out = CARD_OFFSET + (bus.row || 0) * (CARD_H + BUS_GAP);
  switch (bus.move) {
    case 'n': return { x: bus.left - CARD_OFFSET - CARD_W / 2, y: bus.top, w: CARD_W, h: CARD_H };
    case 'e': return { x: bus.left, y: bus.top - out - CARD_H / 2, w: CARD_W, h: CARD_H };
    case 'w': return { x: bus.left, y: bus.top + out + CARD_H / 2, w: CARD_W, h: CARD_H };
    case 's':
    default: return { x: bus.left + CARD_OFFSET + CARD_W / 2, y: bus.top, w: CARD_W, h: CARD_H };
  }
}

/**
 * 帯を、車線ごとの四角に畳む。
 *
 * 車線 1 本につき、そのマスから**右の隣・下の隣**へ 1 枚ずつと、
 * **角・分岐・端には 13px 角の当て**を 1 枚。まっすぐ通り抜けるだけの節に
 * 当ては要らない（隣へ伸ばした帯どうしがそこで合う）。
 *
 * 伸ばす先は**隣のマスの車線の位置**（マスの中心ではない）。中心まででは
 * 当てとのあいだに 1px 空き、中心を 6.5px 越えさせると**角の外へ出る。**
 *
 * ## 都市高速は路線の上を越える
 *
 * 交差では**都市高速を上に、路線を下に**描く（高架が跨いでいるので）。
 * 返す並びが重ね順なので、路線 → 都市高速の下敷き → 都市高速 → 白抜き。
 *
 * 都市高速には**白い下敷き**を敷く。車線のあいだの 2px から下の路線の色が
 * 覗くのを止めるため、下敷きだけ帯の中心へ 2px 伸ばす（下敷きも車線ごとに
 * 作るので、角の形は車線と同じ — 四角 1 枚で敷くと角の外へ白がはみ出す）。
 *
 * 白抜きの内側（`hole`）は同じ骨組みを 9px で塗り重ねたもの。
 *
 * ## 2 本で引くのは幹と都市高速だけ
 *
 * この 2 本は縦にまっすぐなので、**左（西）が上り・右（東）が下り**が最後まで
 * 成り立つ。そのほかの線は**車線 1 本ぶんの 1 本**にする（`ROAD` の断り）。
 *
 * **2 本で引くのは、両端とも 2 本の線のときだけ。**幹から枝が出るところで
 * 片側だけ 2 本にすると、繋ぎ目で線が食い違う。
 */
export function shapesOf(lines, stops, cx, cy) {
  const { kinds, twins, cells, stubs, ups, crossed } = cellsOf(lines, stops);
  // 重ね順（奥 → 手前）: 支線（1 本の帯）→ 幹（上下 2 本の帯）→ 都市高速。
  // **支線が幹を横切るところでは幹を手前に**（2026-09-23。幹の青が支線の
  // 後ろへ回り込んで見えた）。都市高速はこれまでどおりいちばん手前
  const under = [];
  const trunk = [];
  const cases = [];
  const over = [];
  const holes = [];
  const laneAt = (cell, side) => laneOf(kinds, cx, cy, cell, side, crossed);
  // 横の車線 `side` が、そのマスのどこを通るか。**┐ と └ の角では組が逆**
  // （縦の側で数えた `laneOf` の反対側。`flippedCorner`）
  const hLane = (cell, side) => laneAt(
    cell, flippedCorner(kinds, cell, crossed) ? -side : side
  );
  const twin = (row, col) => twins.has(`${row}/${col}`);

  /**
   * 1 枚を 3 層（下敷き・帯・白抜き）に積む。`span` は中心と太さを受けて
   * 四角を返す。色は**その側を走る向き**（1 本の線は向きを持たないので道の色）
   */
  const draw = (cell, side, key, span, axis, pos) => {
    const express = cell.kind === "express";
    // `pos` は車線の位置を直に渡すとき（ランプのつなぎの端）
    const me = pos ?? laneAt(cell, side);
    // 下敷きを伸ばす向き。**車線がずれている軸だけ**、帯の中心へ 2px
    const ex = me.x === cx(cell.col) ? 0 : BAR_GAP;
    const ey = me.y === cy(cell.row) ? 0 : BAR_GAP;
    const shift = (e) => -side * e / 2;
    const lane = side === 0
      ? null
      : laneOfSide(axis, side, ups.get(`${cell.row}/${cell.col}`));
    const at = { kind: cell.kind, lane: lane ? lane.key : "one" };

    if (express) {
      cases.push({
        ...at, ...span(me, BAR + ex, BAR + ey, shift(ex), shift(ey)),
        key: `c/${key}`, color: "#fff", casing: true,
      });
    }
    // 2 本で引く片側（side が ±1）は幹、1 本（side 0）は支線
    (express ? over : side === 0 ? under : trunk).push({
      ...at, ...span(me, BAR, BAR, 0, 0),
      key, color: lane ? lane.color : ROAD,
    });
    if (express) {
      holes.push({
        ...at, ...span(me, HOLE, HOLE, 0, 0),
        key: `w/${key}`, color: "#fff", hole: true,
      });
    }
  };

  for (const cell of cells) {
    // 側は**1 枚ごと**に決める。1 本の線は中心（0）に 1 枚、2 本の線は
    // 北西と南東に 1 枚ずつ
    const at = `${cell.row}/${cell.col}`;
    const me = laneAt(cell, 0);   // 隣の様子は側に依らない
    const self = cell.twin ? [-1, 1] : [0];

    if (! me.pass) {
      self.forEach((side, i) => draw(cell, side, `o/${at}/${i}`,
        (p, w, h, dx, dy) => ({
          left: p.x - w / 2 + dx, top: p.y - h / 2 + dy, width: w, height: h,
        }), me.axis));
    }

    // **隣へ伸ばす帯は、両端とも 2 本の線のときだけ 2 本。**幹から枝が
    // 出るところで幹の側だけ 2 本にすると、**枝の 1 マス目が 2 本線になる**
    // （2026-09-19 に指摘を受けて直した）
    if (me.right) {
      // **交差では 2 マス先まで伸ばす**（me.right が 2 になる）
      const far = { ...cell, col: cell.col + me.right };
      const both = cell.twin && twin(far.row, far.col);
      (both ? [-1, 1] : [0]).forEach((side, i) => {
        const to = hLane(far, side).x;
        draw(cell, side, `h/${at}/${i}`, (p, w, h, dx, dy) => ({
          left: p.x, top: p.y - h / 2 + dy, width: to - p.x, height: h,
        }), "h", hLane(cell, side));
      });
    }
    if (me.down) {
      const far = { ...cell, row: cell.row + me.down };
      const both = cell.twin && twin(far.row, far.col);
      (both ? [-1, 1] : [0]).forEach((side, i) => {
        const to = laneAt(far, side).y;
        draw(cell, side, `v/${at}/${i}`, (p, w, h, dx, dy) => ({
          left: p.x - w / 2 + dx, top: p.y, width: w, height: to - p.y,
        }), "v");
      });
    }
  }

  /* **ランプのつなぎの端。**都市高速の帯を、つなぐ先の線の**縁まで**伸ばす
     （種類が違うので上の繋ぎでは結ばれない）。中心まで伸ばすと白抜きが
     幹の帯の上にかぶる */
  for (const stub of stubs) {
    if (stub.to === null) {
      continue;
    }
    const [fr, fc] = stub.from.split("/").map(Number);
    const [tr, tc] = stub.to.split("/").map(Number);
    const edge = twins.has(stub.to) ? BAND / 2 : BAR / 2;
    const axis = stub.dc !== 0 ? "h" : "v";
    const cell = { row: fr, col: fc, kind: "express" };
    [-1, 1].forEach((side, i) => {
      const pos = axis === "h"
        ? { x: cx(fc), y: cy(fr) + side * LANE_OFF }
        : { x: cx(fc) + side * LANE_OFF, y: cy(fr) };
      const to = axis === "h"
        ? cx(tc) - stub.dc * edge
        : cy(tr) - stub.dr * edge;
      draw(cell, side, `s/${stub.to}/${stub.from}/${i}`, (p, w, h, dx, dy) => (
        axis === "h"
          ? { left: Math.min(p.x, to), top: p.y - h / 2 + dy,
            width: Math.abs(to - p.x), height: h }
          : { left: p.x - w / 2 + dx, top: Math.min(p.y, to),
            width: w, height: Math.abs(to - p.y) }
      ), axis, pos);
    });
  }

  return [...under, ...trunk, ...cases, ...over, ...holes];
}

/**
 * その側の車線を走るのはどちらの向きか。**左側通行なので縦横で入れ替わる。**
 *
 * 縦に走る帯は北西（-1）が北行き＝上り（緑）。横に走る帯は北西（-1）が
 * 東行き＝下り（青）。**2 本で引くのは縦にまっすぐな幹と都市高速だけ**なので、
 * 画面にはほぼ「左が緑・右が青」だけが出る。
 */
function laneOfSide(axis, side, ups) {
  // **都市高速はたどった上りで決める**（`expressUps`）。その側を走る向きが
  // 上りなら緑
  const known = ups?.[axis];
  if (known) {
    const move = axis === "v" ? (side < 0 ? "n" : "s") : (side < 0 ? "e" : "w");

    return move === known ? LANES.backward : LANES.forward;
  }
  const up = axis === "v" ? side < 0 : side > 0;

  return up ? LANES.backward : LANES.forward;
}

export default function LineDiagramView({ data, movers = [], onSelect }) {
  const { box, lines, stops } = data;

  const view = useMemo(() => {
    // 札の置き方（横に走る帯のバス停は縦書き）。**列の幅もこれで変わる**
    const layout = layoutOf(lines, stops);
    const { x, width } = columnsOf(lines, stops, layout);
    // **列の中心は左端から COL_MIN の半分。**停留所の札も同じところから
    // 出すので、線と札がずれない。`PAD` は帯の半分が枠から出るぶん
    const cx = (col) => (x.get(col) ?? 0) + COL_MIN / 2 + PAD;
    const cy = (row) => (row - box.minRow) * ROW_H + ROW_H / 2 + PAD;

    return {
      cx,
      cy,
      layout,
      // 塗ったマスの表と、2 本で引くマス。**バスを置くのにも使う**ので、
      // 図といっしょに作る（250ms ごとに 910 マスを数え直さない）
      grid: cellsOf(lines, stops),
      width: width + COL_MIN + PAD * 2,
      height: (box.maxRow - box.minRow + 2) * ROW_H + PAD * 2,
      shapes: shapesOf(lines, stops, cx, cy),
      marks: marksOf(lines, stops, cx, cy),
      // 幹（3 号線・昭和通り）の横位置。**開いたときにここを真ん中にする**
      axis: box.axis == null ? null : cx(box.axis),
    };
  }, [box, lines, stops]);

  /* 走っているバス。**図とは別に組む** — 250ms ごとに動くので、帯や札まで
     作り直すと重い（帯だけで 910 マス × 車線 2 本） */
  const buses = useMemo(
    () => busesOf(movers, data.corridors, view.grid, view.cx, view.cy),
    [movers, data.corridors, view]
  );

  /* **開いたときは幹を真ん中に置く。**図は 5,700px ほどあるので、左端から
     見せると博多側の端だけが写る。幹は 55 列のうち 16 列目にあり、真ん中で
     はない。

     **30 秒ごとの読み直しで動かしてはいけない。**`view.axis` は数なので、
     図が変わらないかぎり同じ値で、その間この処理は走らない */
  const scroll = useRef(null);
  const [zoom, setZoom] = useState(readZoom);
  // 拡大・縮小の前に見ていた点（図の座標）。**そこを画面の真ん中に保つ**
  const anchor = useRef(null);
  useEffect(() => {
    const el = scroll.current;
    if (! el || view.axis === null) {
      return;
    }
    el.scrollLeft = Math.max(0, view.axis * zoom - el.clientWidth / 2);
    // 倍率が変わったときは下の useLayoutEffect が位置を決める
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.axis]);

  /* **拡大・縮小しても、画面の真ん中に見えていた場所を動かさない。**
     図は横は自分の枠で、縦はページごとスクロールしているので、両方を
     直す。描き直したあと（大きさが変わったあと）に合わせる */
  useLayoutEffect(() => {
    const el = scroll.current;
    const a = anchor.current;
    anchor.current = null;
    if (! el || ! a) {
      return;
    }
    el.scrollLeft = Math.max(0, a.x * zoom - el.clientWidth / 2);
    window.scrollTo({ top: Math.max(0, a.top + a.y * zoom - window.innerHeight / 2) });
  }, [zoom]);

  const zoomBy = (step) => {
    const i = ZOOMS.indexOf(zoom);
    const next = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, (i < 0 ? ZOOMS.indexOf(1) : i) + step))];
    if (next === zoom) {
      return;
    }
    const el = scroll.current;
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY;
      anchor.current = {
        x: (el.scrollLeft + el.clientWidth / 2) / zoom,
        y: (window.scrollY + window.innerHeight / 2 - top) / zoom,
        top,
      };
    }
    setZoom(next);
    saveZoom(next);
  };

  return (
    <div className="ld-scroll" ref={scroll}>
      {/* **拡大・縮小のボタン。**画面の右の縦中央に浮かせる（図は縦には
          ページごとスクロールするので、図の中に置くと見えなくなる） */}
      <div className="ld-zoomctl" role="group" aria-label="路線図の拡大・縮小">
        <button
          type="button"
          className="ld-zoomctl__btn"
          onClick={() => zoomBy(1)}
          disabled={zoom >= ZOOMS[ZOOMS.length - 1]}
          aria-label="拡大"
          title={`拡大（いま ${Math.round(zoom * 100)}%）`}
        >
          <ZoomIcon plus />
        </button>
        <button
          type="button"
          className="ld-zoomctl__btn"
          onClick={() => zoomBy(-1)}
          disabled={zoom <= ZOOMS[0]}
          aria-label="縮小"
          title={`縮小（いま ${Math.round(zoom * 100)}%）`}
        >
          <ZoomIcon />
        </button>
      </div>
      {/* **拡大・縮小は CSS の scale で。**中身の座標（帯・札・バス）は
          等倍のまま持ち、外側の箱だけを倍率ぶんの大きさにしてスクロールを
          合わせる。座標を倍率で掛け直すと、バスを置く計算まで全部変わる */}
      <div
        className="ld-zoom"
        style={{ width: view.width * zoom, height: view.height * zoom }}
      >
      <div
        className="ld-map"
        style={{
          width: view.width,
          height: view.height,
          transform: zoom === 1 ? undefined : `scale(${zoom})`,
        }}
      >
        {view.shapes.map((s) => (
          <span
            key={s.key}
            className={[
              "ld-bar", s.hole ? "is-hole" : "", s.casing ? "is-casing" : "",
            ].filter(Boolean).join(" ")}
            style={{
              left: s.left, top: s.top, width: s.width, height: s.height,
              background: s.color,
            }}
          />
        ))}

        {/* 停まる向きの三角。帯の上に白抜きで置く */}
        {view.marks.map((m) => (
          <span
            key={m.key}
            className={`ld-tri is-${m.axis} is-${m.dir}`}
            style={{ left: m.left, top: m.top, "--ld-ink": m.ink }}
            aria-hidden="true"
          />
        ))}

        {/* バス停のあいだの所要。**帯の上に白い小札で置く**（節番号の線の
            .lm-plate__min と同じ見た目）。位置は道のりの真ん中 */}
        {(data.legs || []).map((leg) => (
          <span
            key={leg.at.flat().join("/")}
            className="ld-min"
            style={{
              left: (view.cx(leg.at[0][1]) + view.cx(leg.at[1][1])) / 2,
              top: (view.cy(leg.at[0][0]) + view.cy(leg.at[1][0])) / 2,
            }}
            title={`${leg.from} → ${leg.to} ${leg.minutes}分`}
          >
            {leg.minutes}分
          </span>
        ))}

        {stops.map((s) => (
          <Stop
            key={`${s.row}/${s.col}/${s.label}`}
            stop={s}
            place={view.layout.get(`${s.row}/${s.col}`)}
            // ランプの札は、つなぎが支線に着くマスに置くことがある
            left={view.cx((view.layout.get(`${s.row}/${s.col}`)?.anchor ?? s).col)}
            top={view.cy((view.layout.get(`${s.row}/${s.col}`)?.anchor ?? s).row)}
          />
        ))}

        {/* 走っているバス。**札の上に置く**（札に隠れると追えない） */}
        {buses.map((b) => (
          <Bus key={`${b.bus.trip}/${b.lane}`} bus={b} onSelect={onSelect} />
        ))}
      </div>
      </div>
    </div>
  );
}

/** 倍率の段。**1 を必ず含める**（等倍に戻れるように） */
export const ZOOMS = [0.5, 0.65, 0.8, 1, 1.25, 1.5, 2];

const ZOOM_KEY = "linemap:zoom";

/** 前に選んだ倍率。**覚えていなければ等倍**（読めないブラウザもある） */
function readZoom() {
  try {
    const v = Number(window.localStorage.getItem(ZOOM_KEY));

    return ZOOMS.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

function saveZoom(value) {
  try {
    window.localStorage.setItem(ZOOM_KEY, String(value));
  } catch {
    // 覚えられなくても拡大・縮小はできる
  }
}

/** 虫眼鏡に ＋ / − 。**線だけで描く**（半透明のボタンの上で潰れない） */
function ZoomIcon({ plus = false }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="15.5" y1="15.5" x2="20.5" y2="20.5" />
      <line x1="7.5" y1="10.5" x2="13.5" y2="10.5" />
      {plus && <line x1="10.5" y1="7.5" x2="10.5" y2="13.5" />}
    </svg>
  );
}

/**
 * 進む向きの矢印。**枠がどちら側へ出ていても、向きは矢印で読める。**
 *
 * **絵文字にしない。**▶ / ◀（U+25B6 / U+25C0）は既定で絵文字として描かれる
 * 環境があり、色も大きさも枠から浮く。**異体字セレクタ（U+FE0E）を付けて
 * 字として描かせる。**▲ / ▼（U+25B2 / U+25BC）は既定で字だが、揃えて付ける
 * — 付け忘れた向きだけ絵文字になる、という壊れ方を避ける。
 */
/**
 * 行先番号の欄に出す字。
 *
 * **行先番号を持たない急行がある。**にしてつは新宮・大蔵まわりの都市高速便を
 * 急行と呼ぶが、時刻表には番号が無い。「無番」とだけ出すと、番号を持たない
 * 各駅（香椎花園行き・千早駅前行きなど 70 本）と見分けが付かない。
 * どの便が急行かは API が決める（`LineDiagram::isKyuko`）。
 *
 * 番号も急行でもなければ「無番」。**空けない** — にしてつ自身がそう呼ぶし、
 * 空けると読み手が補ってしまう。
 */
function routeLabel(trip) {
  return trip.route_no || (trip.kyuko ? "急行" : "無番");
}

const ARROW = {
  n: '▲︎', s: '▼︎',
  e: '▶︎', w: '◀︎',
};

/**
 * 1 台のバス。**塗りが観測、輪郭が推定。**
 *
 * 節番号の線と同じ分け方（`.lm-bus` の `is-at` / `is-moving`）。停留所に
 * いるあいだは「その時刻にそこにいた」という観測で、区間を滑っているのは
 * 時刻表の所要で進めた推定。**動いて見えることと、位置が分かっていることは
 * 別**なので、そこを見た目で分ける。
 *
 * ## 中身は節番号の線と同じだけ載せる
 *
 * 営業所の頭 1 文字・車号・行先番号・遅れ。**車号だけでは足りない** —
 * どの系統が何分遅れているかは、開かずに読めるほうがよい。
 *
 * ## 枠は帯の脇、点は帯の上
 *
 * 枠は 118px あり、車線 1 本（13px）には載らない。**帯の脇へ出して、
 * 正確な位置には点を残す。**出す側は**そのバスが走っている車線と同じ**
 * （左側通行。縦の道では左右、横の道では上下）。反対向きの 2 台は反対側の
 * 車線にいるので、枠も重ならない。位置は CSS（`.ld-mover.is-n` など 4 通り）。
 */
function Bus({ bus, onSelect }) {
  const { bus: trip } = bus;
  // 観測の古さで薄くし、端に着いたぶんを掛ける（節番号の線と同じ）
  const opacity =
    (1 - Math.min((trip.age_minutes || 0) / 30, 1) * 0.5) * (bus.fade ?? 1);
  const delay = delayBadge(trip.delay_minutes);

  return (
    <span
      className={[
        'ld-mover', `is-${bus.move}`, `is-${bus.lane}`,
        bus.moving ? 'is-moving' : 'is-at',
        // **推測の便**（時刻表にはあるが位置が取れていない）。節番号の線の
        // `.lm-bus.is-guess` と同じく、枠を縞の破線にして観測と見分ける
        bus.guess ? 'is-guess' : '',
      ].filter(Boolean).join(' ')}
      style={{
        left: bus.left, top: bus.top, opacity,
        '--ld-color': bus.color, '--ld-ink': bus.ink,
        // 横に走るバスの枠を、帯から外側へ何段出すか（`stackBuses`）
        '--ld-row': bus.row || 0,
      }}
    >
      {/* **点が本当の位置。**枠は脇へ出してあるので、そこは点で言う */}
      <span className="ld-mover__dot" aria-hidden="true" />

      <button
        type="button"
        className="ld-bus"
        onClick={() => onSelect && onSelect({
          trip: trip.trip, dir: bus.lane, bus_number: trip.bus_number,
        })}
        title={`${bus.guess ? '【推測】' : ''}`
          + `${trip.bus_number || '車号不明'} ${routeLabel(trip)}`
          + `${trip.destination ? ` ${trip.destination}行き` : ''}`
          + `／${bus.guess ? '位置は時刻表の定刻から' : delay.label}（タップで詳しく）`}
      >
        {/* **推測の印。**節番号の線の `.lm-guess` と同じ見た目。車号の
            出どころ（目撃か乗番の予測か）は枠には出さず、開いたら言う */}
        {bus.guess && (
          <span className="lm-guess ld-bus__guess" aria-label="推測">
            <span className="lm-squeeze">推測</span>
          </span>
        )}
        <span className="ld-bus__arrow">{ARROW[bus.move] || '▼'}</span>
        {/* 営業所の頭 1 文字と車号は**1 つの塊**（「ア1925」と読める） */}
        <span className="ld-bus__id">
          {trip.depot && (
            <span className="ld-bus__depot" title={trip.depot}>
              {Array.from(trip.depot)[0]}
            </span>
          )}
          <span className="ld-bus__no">{trip.bus_number || '？'}</span>
        </span>
        {/* 行先番号。**箱の幅を固定して列を揃える**（節番号の線と同じ） */}
        <span
          className={`ld-bus__route${trip.route_no ? '' : ' is-none'}`
            + (trip.kyuko ? ' is-kyuko' : '')}
        >
          <span
            className="lm-fit"
            style={{ transform: `scaleX(${routeScale(routeLabel(trip))})` }}
          >
            {routeLabel(trip)}
          </span>
        </span>
        {/* 遅れ。**色の段は節番号の線と同じ表**（utils/delayBadge） */}
        <span className={`lm-badge ${delay.cls}`} title={delay.label}>
          {delay.cls === 'd-unknown'
            ? <span className="lm-squeeze lm-squeeze--3to2">{delay.text}</span>
            : delay.text}
        </span>
      </button>
    </span>
  );
}

/**
 * 停留所とランプ。
 *
 * **バス停はすべて白い札**（節番号の線と同じ）。ランプだけ破線の札にする
 * — 時刻表に載っていないので、ランプのどこまで来たかは分からない。
 *
 * 名前は右へ出す。Excel が右へはみ出させて描いてあるので、そこに列の幅を
 * 取ってある。
 */
function Stop({ stop, place, left, top }) {
  if (stop.ramp) {
    return (
      <span className="ld-stop is-ramp" style={{ left, top }}>
        <span className="ld-ramp">{stop.label}</span>
      </span>
    );
  }

  const unknown = stop.stops.length === 0;
  // **横に走る帯のバス停は縦書き。**右へ伸ばすと列がまるごと広がる
  const vertical = place?.vertical
    ? ` is-vertical ${place.down ? "is-down" : "is-up"}`
    : place?.left ? " is-left" : "";
  // **2 段組み**（`label_lines`）。縦書きなら 2 列になる
  const lines = linesOf(stop);

  return (
    <span
      className={`ld-stop${unknown ? " is-unknown" : ""}`}
      style={{ left, top }}
    >
      <span
        className={`ld-plate${vertical}${lines.length > 1 ? " is-multi" : ""}`}
        title={lines.length > 1 ? stop.label : undefined}
      >
        {lines.length > 1
          ? lines.map((t) => <span key={t}>{t}</span>)
          : stop.label}
      </span>
    </span>
  );
}

/**
 * 凡例。**帯は道（1 色）、色が付くのは向きを持つものだけ。**
 *
 * 帯を上り・下りで塗り分けられない理由は `ROAD` に書いてある。
 */
export function DiagramLegend({ stops }) {
  const unknown = (stops || []).filter((s) => s.stops.length === 0 && ! s.ramp).length;
  const bar = (style, cls = "") => (
    <span className={`lm-legend__bar${cls}`} style={style} />
  );

  /* **凡例は記号と短い言葉だけ。**文で説明しない（2026-09-23）。
     上下 2 本で引くのは幹と都市高速だけ（`ROAD` に理由）で、どちらも
     左（西）が緑＝上り・右（東）が青＝下り */
  return (
    <div className="lm-legend">
      <span className="lm-legend__item">
        {bar({ background: LANES.backward.color })}
        {bar({ background: LANES.forward.color })}
        {/* 上下 2 本の幹。**道の名前で書く**（2026-09-23） */}
        昭和通・3号線・495号線
      </span>
      <span className="lm-legend__item">
        {bar({ "--ld-color": LANES.backward.color }, " lm-legend__bar--hollow")}
        {bar({ "--ld-color": LANES.forward.color }, " lm-legend__bar--hollow")}
        都市高速
      </span>
      <span className="lm-legend__item">
        {bar({ background: ROAD })}
        路線
      </span>
      {unknown > 0 && (
        <span className="lm-legend__item">
          {bar({}, " lm-legend__bar--dashed")}
          路線データ無しバス停 {unknown}
        </span>
      )}
      <span className="lm-legend__note">
        実線＝停車中／破線＝移動中／推測付き＝位置情報未取得
      </span>
    </div>
  );
}
